import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon, InfoIcon, PencilIcon, TriangleDownIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, FormControl, Stack, Textarea} from '@primer/react'
import {useCallback, useEffect, useRef} from 'react'
import invariant from 'tiny-invariant'

import type {PersistedOption} from '../../../api/columns/contracts/single-select'
import type {SuggestedRepository} from '../../../api/repository/contracts'
import {AutoAssignWorkflowField, AutoAssignWorkflowFieldChangeName} from '../../../api/stats/contracts'
import {
  type ConfiguredModelUnavailableWorkflowNotice,
  MemexActionType,
  type MemexWorkflowAction,
  MemexWorkflowNoticeType,
} from '../../../api/workflows/contracts'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {useWorkflowSetFieldAction} from '../../../hooks/use-workflow-action'
import {useAutomationGraph} from '../../../state-providers/workflows/use-automation-graph'
import {useWorkflows} from '../../../state-providers/workflows/use-workflows'
import {WorkflowResources} from '../../../strings'
import {AgentRepositoryPicker} from '../agent-repository-picker'
import {Connector} from '../connector'
import {ModelPicker} from '../model-picker'
import {StatusPicker} from '../status-picker'
import styles from './assign-copilot-block.module.css'
import {AutomationBlock} from './automation-block'

const MAX_INSTRUCTIONS_LENGTH = 4000
// Output mode is hardcoded for the MVP — only "create a pull request" is supported.
const DEFAULT_OUTPUT_MODE = 'pull_request'

function isConfiguredModelUnavailableNotice(notice: {
  type?: unknown
}): notice is Partial<ConfiguredModelUnavailableWorkflowNotice> & {
  type: typeof MemexWorkflowNoticeType.ConfiguredModelUnavailable
} {
  return notice.type === MemexWorkflowNoticeType.ConfiguredModelUnavailable
}

export const AssignCopilotBlock = () => {
  const {isEditing, workflow, setWorkflow} = useAutomationGraph()
  const {getRecommendedFieldOptionIdForTriggerType} = useWorkflows()
  const {postStats} = usePostStats()

  const trackFieldChange = useCallback(
    (field: (typeof AutoAssignWorkflowField)[keyof typeof AutoAssignWorkflowField]) => {
      postStats({
        name: AutoAssignWorkflowFieldChangeName,
        context: field,
      })
    },
    [postStats],
  )

  const getProjectItemsAction = workflow.actions.find(a => a.actionType === MemexActionType.GetProjectItems)
  const assignCopilotAction = workflow.actions.find(a => a.actionType === MemexActionType.AssignCopilot)
  invariant(assignCopilotAction, 'AssignCopilot action must be defined')
  invariant(getProjectItemsAction, 'GetProjectItems action must be defined')

  const {column, selectedOption: sourceOption} = useWorkflowSetFieldAction(getProjectItemsAction)
  const statusOptions = column && 'options' in column.settings ? column.settings.options : []
  const destinationOptions = sourceOption
    ? statusOptions.filter(option => option.id !== sourceOption.id)
    : statusOptions
  const destinationOption = destinationOptions.find(
    option => option.id === assignCopilotAction.arguments.destinationFieldOptionId,
  )
  const recommendedOptionId = getRecommendedFieldOptionIdForTriggerType(workflow.triggerType)
  const instructions = assignCopilotAction.arguments.instruction ?? ''
  const allowedRepositories = assignCopilotAction.arguments.allowedRepositories
  const selectedRepoNwo = allowedRepositories?.[0]
  const selectedModelId = assignCopilotAction.arguments.model

  // Update an action in-place by actionType. We use setWorkflow + actionType
  // matching here (instead of the shared replaceAction helper, which matches by
  // action.id) because auto-assign-agent's default workflow has two actions and unsaved
  // default actions all have action.id === undefined — id-based matching would
  // conflate them.
  const updateActionByType = useCallback(
    (actionType: MemexActionType, updater: (a: MemexWorkflowAction) => MemexWorkflowAction) => {
      setWorkflow(prev => {
        const actions = prev.actions.map(a => (a.actionType === actionType ? updater(a) : a))
        return {...prev, actions}
      })
    },
    [setWorkflow],
  )

  const onSourceColumnSelected = (option: PersistedOption | undefined) => {
    trackFieldChange(AutoAssignWorkflowField.SourceColumn)
    setWorkflow(prev => {
      const actions = prev.actions.map(a => {
        if (a.actionType === MemexActionType.GetProjectItems) {
          return {
            ...a,
            arguments: {...a.arguments, fieldOptionId: option?.id},
          }
        }

        if (
          a.actionType === MemexActionType.AssignCopilot &&
          option?.id &&
          a.arguments.destinationFieldOptionId === option.id
        ) {
          return {
            ...a,
            arguments: {...a.arguments, destinationFieldOptionId: undefined},
          }
        }

        return a
      })

      return {...prev, actions}
    })
  }

  const onDestinationColumnSelected = (option: PersistedOption | undefined) => {
    trackFieldChange(AutoAssignWorkflowField.DestinationColumn)
    updateActionByType(MemexActionType.AssignCopilot, a => ({
      ...a,
      arguments: {...a.arguments, destinationFieldOptionId: option?.id},
    }))
  }

  const onInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    updateActionByType(MemexActionType.AssignCopilot, a => ({
      ...a,
      arguments: {
        ...a.arguments,
        instruction: value,
        outputMode: DEFAULT_OUTPUT_MODE,
      },
    }))
  }

  // Track instructions changes on blur (rather than on every keystroke) to avoid stat spam
  // while still attributing edits to the instructions field. Capture the pre-edit value on focus
  // so each editing session is independent.
  const instructionsAtFocusRef = useRef(instructions)
  const onInstructionsFocus = useCallback(() => {
    instructionsAtFocusRef.current = instructions
  }, [instructions])
  const onInstructionsBlur = useCallback(() => {
    if (instructions !== instructionsAtFocusRef.current) {
      trackFieldChange(AutoAssignWorkflowField.Instructions)
    }
  }, [instructions, trackFieldChange])

  const onRepositorySelected = useCallback(
    (repo: SuggestedRepository) => {
      if (selectedRepoNwo === repo.nameWithOwner) return

      trackFieldChange(AutoAssignWorkflowField.Repository)
      updateActionByType(MemexActionType.AssignCopilot, a => ({
        ...a,
        arguments: {
          ...a.arguments,
          allowedRepositories: [repo.nameWithOwner],
          // Clear model when repo changes — the previous model may not be available in the new repo
          model: undefined,
        },
      }))
    },
    [selectedRepoNwo, trackFieldChange, updateActionByType],
  )

  const onRepositoryClear = useCallback(() => {
    trackFieldChange(AutoAssignWorkflowField.Repository)
    updateActionByType(MemexActionType.AssignCopilot, a => ({
      ...a,
      arguments: {
        ...a.arguments,
        allowedRepositories: undefined,
        model: undefined,
      },
    }))
  }, [trackFieldChange, updateActionByType])

  const onModelSelected = useCallback(
    (modelId: string | undefined) => {
      trackFieldChange(AutoAssignWorkflowField.Model)
      updateActionByType(MemexActionType.AssignCopilot, a => ({
        ...a,
        arguments: {
          ...a.arguments,
          model: modelId,
        },
      }))
    },
    [trackFieldChange, updateActionByType],
  )

  const configuredModelUnavailableNotice = workflow.notices?.find(isConfiguredModelUnavailableNotice)
  const hasConfiguredModelUnavailableNotice = configuredModelUnavailableNotice !== undefined
  const configuredModelUnavailableMessage =
    typeof configuredModelUnavailableNotice?.message === 'string' && configuredModelUnavailableNotice.message.length > 0
      ? configuredModelUnavailableNotice.message
      : WorkflowResources.assignCopilotConfiguredModelUnavailableWarning
  useEffect(() => {
    if (hasConfiguredModelUnavailableNotice) {
      announce(configuredModelUnavailableMessage)
    }
  }, [configuredModelUnavailableMessage, hasConfiguredModelUnavailableNotice])

  return (
    <>
      <AutomationBlock
        icon={PencilIcon}
        iconBg="var(--bgColor-attention-muted)"
        iconColor="var(--fgColor-attention)"
        headerDescription={WorkflowResources.autoAssignAgentStatusUpdatedLabel}
        {...testIdProps('automation-assign-copilot-block')}
      >
        <StatusPicker
          onStatusSelected={onSourceColumnSelected}
          options={statusOptions}
          selectedOption={sourceOption ? {...sourceOption} : undefined}
          isEditing={isEditing}
          headerDescription={WorkflowResources.assignCopilotSourceColumnLabel}
          testId="workflow-auto-assign-agent-source-column"
          recommendedOptionId={recommendedOptionId}
        />
      </AutomationBlock>

      <Connector />

      <AutomationBlock
        icon={CopilotIcon}
        iconBg="var(--bgColor-success-muted)"
        iconColor="var(--fgColor-success)"
        headerDescription={WorkflowResources.assignCopilotAssignAgentHeading}
        headerEnd={
          <Stack direction="horizontal" align="center" gap="condensed" wrap="wrap" className={styles.assignAgentInfo}>
            <InfoIcon size={14} />
            <span>{WorkflowResources.assignCopilotAssignAgentInfo}</span>
          </Stack>
        }
      >
        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          gap="normal"
          wrap="wrap"
          className={styles.formRow}
        >
          <Stack.Item grow>
            <FormControl disabled>
              <FormControl.Label>{WorkflowResources.assignCopilotAgentLabel}</FormControl.Label>
              <FormControl.Caption>{WorkflowResources.assignCopilotAgentCaption}</FormControl.Caption>
            </FormControl>
          </Stack.Item>
          <Button
            leadingVisual={CopilotIcon}
            trailingVisual={TriangleDownIcon}
            disabled
            {...testIdProps('workflow-auto-assign-agent-agent-selector')}
          >
            {WorkflowResources.assignCopilotAgentCopilot}
          </Button>
        </Stack>

        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          gap="normal"
          wrap="wrap"
          className={styles.formRow}
        >
          <Stack.Item grow>
            <FormControl disabled={!isEditing}>
              <FormControl.Label>{WorkflowResources.assignCopilotRepositoryLabel}</FormControl.Label>
              <FormControl.Caption>{WorkflowResources.assignCopilotRepositoryCaption}</FormControl.Caption>
            </FormControl>
          </Stack.Item>
          <AgentRepositoryPicker
            onRepositorySelected={onRepositorySelected}
            onRepositoryCleared={onRepositoryClear}
            selectedRepoNwo={selectedRepoNwo}
            isEditing={isEditing}
            headerDescription={WorkflowResources.assignCopilotRepositoryLabel}
          />
        </Stack>
        {hasConfiguredModelUnavailableNotice && (
          <Flash variant="warning" {...testIdProps('workflow-auto-assign-agent-model-notice')}>
            {configuredModelUnavailableMessage}
          </Flash>
        )}

        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          gap="normal"
          wrap="wrap"
          className={styles.formRow}
        >
          <Stack.Item grow>
            <FormControl disabled={!isEditing || !selectedRepoNwo}>
              <FormControl.Label>{WorkflowResources.assignCopilotModelLabel}</FormControl.Label>
              <FormControl.Caption>
                {selectedRepoNwo
                  ? WorkflowResources.assignCopilotModelCaption
                  : WorkflowResources.assignCopilotModelSelectRepoHint}
              </FormControl.Caption>
            </FormControl>
          </Stack.Item>
          <ModelPicker
            repoNameWithOwner={selectedRepoNwo}
            selectedModelId={selectedModelId}
            onModelSelected={onModelSelected}
            isEditing={isEditing}
            headerDescription={WorkflowResources.assignCopilotModelLabel}
          />
        </Stack>

        <div className={styles.formRow}>
          <FormControl>
            <FormControl.Label>{WorkflowResources.assignCopilotInstructionsLabel}</FormControl.Label>
            <FormControl.Caption>{WorkflowResources.assignCopilotInstructionsCaption}</FormControl.Caption>
            <Textarea
              value={instructions}
              onChange={onInstructionsChange}
              onFocus={onInstructionsFocus}
              onBlur={onInstructionsBlur}
              placeholder={WorkflowResources.assignCopilotInstructionsPlaceholder}
              maxLength={MAX_INSTRUCTIONS_LENGTH}
              disabled={!isEditing}
              className={styles.instructionsTextarea}
              resize="vertical"
              rows={5}
              aria-label={WorkflowResources.assignCopilotInstructionsLabel}
              {...testIdProps('workflow-auto-assign-agent-instructions')}
            />
          </FormControl>
        </div>
      </AutomationBlock>

      <Connector />

      <AutomationBlock
        icon={PencilIcon}
        iconBg="var(--bgColor-attention-muted)"
        iconColor="var(--fgColor-attention)"
        headerDescription={WorkflowResources.setValueLabel}
        {...testIdProps('automation-assign-copilot-destination-block')}
      >
        <StatusPicker
          onStatusSelected={onDestinationColumnSelected}
          options={destinationOptions}
          selectedOption={destinationOption ? {...destinationOption} : undefined}
          isEditing={isEditing}
          headerDescription={WorkflowResources.setValueLabel}
          testId="workflow-auto-assign-agent-destination-column"
          recommendedOptionId={recommendedOptionId}
        />
      </AutomationBlock>
    </>
  )
}
